import { useEffect, useState, useRef } from "react";

interface TextScrambleProps {
  text: string;
  className?: string;
  delay?: number;
  scrambleDuration?: number;
}

const chars = "abcdefghijklmnopqrstuvwxyz";

export const TextScramble = ({ 
  text, 
  className = "", 
  delay = 0,
  scrambleDuration = 1500
}: TextScrambleProps) => {
  const [displayText, setDisplayText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const frameRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      startTimeRef.current = null;
      
      const animate = (timestamp: number) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp;
        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / scrambleDuration, 1);
        
        const revealedLength = Math.floor(progress * text.length);
        
        let result = "";
        for (let i = 0; i < text.length; i++) {
          if (i < revealedLength) {
            result += text[i];
          } else if (text[i] === " ") {
            result += " ";
          } else {
            result += chars[Math.floor(Math.random() * chars.length)];
          }
        }
        
        setDisplayText(result);
        
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayText(text);
          setIsComplete(true);
        }
      };
      
      frameRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frameRef.current);
    };
  }, [text, delay, scrambleDuration]);

  return (
    <span className={`${className} ${!isComplete ? 'scramble-cursor' : ''}`} style={{ whiteSpace: 'nowrap' }}>
      {displayText || text}
    </span>
  );
};
